import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { fetchCollection, getLocalized } from '../lib/cms'
import { updateMeta } from '../lib/utils'
import TrustBar from '../components/TrustBar'
import ContactForm from '../components/ContactForm'
import ScrollReveal from '../components/ScrollReveal'
import { 
  Calculator, 
  Percent, 
  FileText, 
  FileCheck, 
  TrendingUp, 
  Users, 
  Briefcase, 
  ArrowRight, 
  ArrowUpRight,
  Check, 
  Star
} from 'lucide-react'

const getServicePathForPlan = (planTitle) => {
  const title = typeof planTitle === 'object' ? planTitle.da : planTitle;
  if (title?.includes('Årsopgørelse') || title?.includes('Forskudsopgørelse')) {
    return '/ydelser/personlig-selvangivelse';
  }
  if (title === 'Årsregnskab') {
    return '/aarsrapport-og-aarsregnskab';
  }
  if (title?.includes('bogføring') || title?.includes('regnskab og bogføring')) {
    return '/regnskabsservice';
  }
  return '/ydelser';
};

// Map icon names to Lucide components
const iconMap = {
  Calculator,
  Percent,
  FileText,
  FileCheck,
  TrendingUp,
  Users,
  Briefcase
}

function TestimonialCard({ testimonial }) {
  const [isExpanded, setIsExpanded] = useState(false)
  const text = getLocalized(testimonial.text) || ''
  const shouldTruncate = text.length > 150
  const displayText = isExpanded ? text : (shouldTruncate ? text.slice(0, 150) + '...' : text)

  return (
    <div className="h-full rounded-none border border-stone-200 bg-white p-6 sm:p-8 shadow-sm hover:shadow-md transition-shadow duration-300 flex flex-col justify-between">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-0.5 text-[#18724a]">
            {[...Array(testimonial.rating || 5)].map((_, i) => (
              <Star key={i} className="h-4 w-4 fill-current" />
            ))}
          </div>
          <span className="text-xs text-stone-400">{testimonial.date}</span>
        </div>
        <div>
          <p className="text-stone-700 italic leading-relaxed inline">
            "{displayText}"
          </p>
          {shouldTruncate && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-[#18724a] hover:text-[#135a3a] font-semibold text-xs ml-2 underline focus:outline-none cursor-pointer inline-block"
            >
              {isExpanded ? 'Vis mindre' : 'Læs mere'}
            </button>
          )}
        </div>
      </div>
      <div className="mt-6 pt-4 border-t border-stone-200/60 flex items-center gap-3">
        <div>
          <div className="font-semibold text-stone-900 text-sm">{testimonial.name}</div>
          <div className="text-xs text-stone-500">{testimonial.company}</div>
        </div>
      </div>
    </div>
  )
}

export default function Home() {
  const [services, setServices] = useState([])
  const [testimonials, setTestimonials] = useState([])
  const [pricingPlans, setPricingPlans] = useState([])
  const [loading, setLoading] = useState(true)
  const [showAllServices, setShowAllServices] = useState(false)
  const [testimonialIndex, setTestimonialIndex] = useState(0)
  const [itemsToShow, setItemsToShow] = useState(3)
  const [isTransitioning, setIsTransitioning] = useState(true)

  useEffect(() => {
    function handleResize() {
      if (window.innerWidth >= 1024) {
        setItemsToShow(3)
      } else if (window.innerWidth >= 768) {
        setItemsToShow(2)
      } else {
        setItemsToShow(1)
      }
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Auto-play effect for testimonials
  useEffect(() => {
    if (testimonials.length === 0) return
    const interval = setInterval(() => {
      setIsTransitioning(true)
      setTestimonialIndex((prev) => prev + 1)
    }, 4000) // Advances every 4 seconds
    return () => clearInterval(interval)
  }, [testimonials.length])

  // Handle seamless loop reset
  useEffect(() => {
    if (testimonials.length === 0) return
    if (testimonialIndex >= testimonials.length) {
      const timer = setTimeout(() => {
        setIsTransitioning(false)
        setTestimonialIndex(0)
      }, 500) // Matches transition duration
      return () => clearTimeout(timer)
    }
  }, [testimonialIndex, testimonials.length])

  // Re-enable transition after reset
  useEffect(() => {
    if (!isTransitioning) {
      const timer = setTimeout(() => {
        setIsTransitioning(true)
      }, 50)
      return () => clearTimeout(timer)
    }
  }, [isTransitioning])

  useEffect(() => {
    updateMeta(
      "Thellefsen Regnskab | Revisor & Bogholder",
      "Personlig revisor og bogholder til fast pris. Vi hjælper mindre virksomheder og selvstændige med årsregnskab, moms og bogføring."
    )
    async function loadData() {
      try {
        const [servicesData, testimonialsData, pricingData] = await Promise.all([
          fetchCollection('services'),
          fetchCollection('testimonials'),
          fetchCollection('pricing_plans')
        ])
        setServices(servicesData)
        setTestimonials(testimonialsData)
        
        // Sort pricing plans: Årsregnskab, Årsregnskab og bogføring, Årsopgørelse & Forskudsopgørelse
        const orderMap = {
          'Årsregnskab': 1,
          'Årsregnskab og bogføring': 2,
          'Årsopgørelse & Forskudsopgørelse': 3
        }
        const sortedPricing = [...pricingData].sort((a, b) => {
          const titleA = getLocalized(a.title) || ''
          const titleB = getLocalized(b.title) || ''
          return (orderMap[titleA] || 99) - (orderMap[titleB] || 99)
        })
        setPricingPlans(sortedPricing)
      } catch (error) {
        console.error('Error loading homepage data:', error)
      } finally {
        setLoading(false)
      }
    }
    loadData()
  }, [])

  return (
    <div className="bg-stone-50 text-stone-800 min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-stone-50 border-b border-stone-200 lg:min-h-[800px] lg:flex lg:items-center shadow-none">
        {/* Decorative background blur */}
        <div className="absolute top-0 left-0 -z-10 h-[600px] w-[600px] rounded-full bg-orange-600/5 blur-3xl" />

        {/* Left Content Column inside standard container */}
        <div className="mx-auto max-w-7xl w-full px-4 sm:px-6 lg:px-8 py-16 lg:py-28">
          <div className="lg:w-1/2 lg:pr-12">
            <div className="max-w-2xl lg:max-w-xl mx-auto lg:mx-0">
              <ScrollReveal direction="up" delay={0} duration={800}>
                <div className="space-y-8 text-center lg:text-left">
                  <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-stone-900 leading-[1.15]">
                    Revisor til<br />fast årlig pris
                  </h1>
                  
                  <p className="text-lg sm:text-xl text-stone-600 leading-relaxed">
                    Få en fast og personlig revisor med mere end 10 års erfaring, som følger din virksomhed hele vejen. Du får den samme kontaktperson år efter år, til en fast årlig pris på 4.000-8.000 kr. ex moms uden overraskelser.
                  </p>

                  {/* Sektion om gratis regnskabsprogram & Bogføringslov */}
                  <div className="bg-orange-50/70 border border-orange-200/80 border-l-4 border-l-orange-600 p-4 sm:p-5 text-left">
                    <div className="flex items-start gap-3">
                      <div className="h-6 w-6 rounded-full bg-orange-600/10 text-orange-600 flex items-center justify-center shrink-0 mt-0.5">
                        <Check className="h-4 w-4 stroke-[2.5]" />
                      </div>
                      <p className="text-sm sm:text-base text-stone-800 leading-relaxed">
                        Vælger jeres virksomhed Thellefsen Regnskab er der i prisen inkluderet gratis regnskabsprogram. Det betyder, at jeres virksomhed overholder den nye Bogføringslov og får en besparelse på ca. 4.000 kr. om året.
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 pt-2">
                    <Link
                      to="/kontakt"
                      className="group w-full sm:w-auto inline-flex items-center justify-center gap-3 rounded-none bg-orange-600 px-8 py-4 text-base font-semibold text-white shadow-md hover:bg-orange-700 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-orange-600/10 active:scale-98"
                    >
                      <span>Få et uforpligtende tilbud</span>
                      <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1.5" />
                    </Link>
                    <Link
                      to="/priser"
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-none bg-white border border-stone-300 px-7 py-4 text-base font-semibold text-stone-800 hover:bg-stone-100 transition-all duration-200"
                    >
                      <span>Se priser</span>
                    </Link>
                  </div>

                  {/* Mini review in hero */}
                  {testimonials.length > 0 && (
                    <div className="pt-6 border-t border-stone-200/80 max-w-md mx-auto lg:mx-0">
                      <div className="flex items-center gap-1 text-[#18724a] mb-1.5 justify-center lg:justify-start">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="h-4 w-4 fill-current" />
                        ))}
                      </div>
                      <p className="text-sm italic text-stone-600">
                        "Thellefsen Regnskab har helt fra start virket ærlig og kompetent. Kort sagt føler jeg mig i gode hænder og kan varmt anbefale Thellefsen regnskab."
                      </p>
                      <p className="text-xs font-semibold text-stone-800 mt-1">
                        — Søren Deleuran
                      </p>
                    </div>
                  )}
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>

        {/* Right Image Column - Flush to edges */}
        <div className="relative lg:absolute lg:inset-y-0 lg:right-0 lg:w-1/2 h-[450px] lg:h-full">
          <ScrollReveal direction="up" delay={200} duration={800} className="h-full w-full">
            <div className="relative h-full w-full">
              <img
                src="https://assets.auxodev.dk/assets/57eb0739-2c0e-4813-8144-be81ba542e2d/bogfoering-og-aarsregnskab-arbejdsbord.jpg"
                alt="Bogføring og årsregnskab med lommeregner, kuglepen og finansielle oversigter"
                className="absolute inset-0 h-full w-full object-cover"
              />
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Trust Bar */}
      <ScrollReveal delay={100} duration={600}>
        <TrustBar />
      </ScrollReveal>

      {/* Services Overview Section - Interne links til alle undersider under ydelser */}
      <section className="py-20 bg-white border-b border-stone-200" id="ydelser">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <ScrollReveal direction="up">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
              <div className="max-w-2xl space-y-3">
                <span className="text-xs font-bold tracking-widest uppercase text-stone-900">
                  Vores ydelser
                </span>
                <h2 className="text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight">
                  Alt inden for regnskab, bogføring og skat
                </h2>
                <p className="text-stone-600 text-base sm:text-lg leading-relaxed">
                  Uanset om du søger hjælp til en enkelt opgave eller ønsker at udlicitere hele dit regnskab til en fast årlig pris, står Peter klar til at hjælpe dig.
                </p>
              </div>
              <div className="shrink-0">
                <Link
                  to="/ydelser"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-orange-600 hover:text-orange-700 underline underline-offset-4"
                >
                  Se alle ydelser & priser
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 items-stretch">
            {/* Ydelse 1: Samlet Regnskabsservice */}
            <ScrollReveal direction="up" delay={100} className="flex">
              <Link
                to="/regnskabsservice"
                className="group w-full bg-stone-50 border border-stone-200 p-6 sm:p-7 flex flex-col justify-between hover:border-orange-500/60 hover:bg-white hover:shadow-md transition-all duration-300"
              >
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="h-12 w-12 bg-white border border-stone-200 flex items-center justify-center text-orange-600 group-hover:bg-orange-50 transition-colors">
                      <Briefcase className="h-6 w-6" />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-stone-900 group-hover:text-orange-600 transition-colors flex items-center justify-between">
                    <span>Regnskabsservice</span>
                    <ArrowUpRight className="h-4 w-4 text-stone-400 group-hover:text-orange-600 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </h3>
                  <p className="text-sm text-stone-600 leading-relaxed mt-2.5">
                    Komplet løsning med løbende bogføring, moms, årsregnskab og selvangivelse til en fast årlig pris inkl. regnskabsprogram.
                  </p>
                </div>
                <div className="mt-6 pt-4 border-t border-stone-200/60 flex items-center text-xs font-bold text-orange-600 group-hover:text-orange-700">
                  <span>Læs mere om regnskabsservice &rarr;</span>
                </div>
              </Link>
            </ScrollReveal>

            {/* Ydelse 2: Bogføring & Moms */}
            <ScrollReveal direction="up" delay={200} className="flex">
              <Link
                to="/bogfoering"
                className="group w-full bg-stone-50 border border-stone-200 p-6 sm:p-7 flex flex-col justify-between hover:border-orange-500/60 hover:bg-white hover:shadow-md transition-all duration-300"
              >
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="h-12 w-12 bg-white border border-stone-200 flex items-center justify-center text-orange-600 group-hover:bg-orange-50 transition-colors">
                      <FileCheck className="h-6 w-6" />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-stone-900 group-hover:text-orange-600 transition-colors flex items-center justify-between">
                    <span>Bogføring & moms</span>
                    <ArrowUpRight className="h-4 w-4 text-stone-400 group-hover:text-orange-600 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </h3>
                  <p className="text-sm text-stone-600 leading-relaxed mt-2.5">
                    Slip for besværet med bilag, bankafstemninger og momsfrister. Vi sikrer, at dit regnskab altid er ajourført og fejlfrit.
                  </p>
                </div>
                <div className="mt-6 pt-4 border-t border-stone-200/60 flex items-center text-xs font-bold text-orange-600 group-hover:text-orange-700">
                  <span>Læs mere om bogføring &rarr;</span>
                </div>
              </Link>
            </ScrollReveal>

            {/* Ydelse 3: Årsrapport & Årsregnskab */}
            <ScrollReveal direction="up" delay={300} className="flex">
              <Link
                to="/aarsrapport-og-aarsregnskab"
                className="group w-full bg-stone-50 border border-stone-200 p-6 sm:p-7 flex flex-col justify-between hover:border-orange-500/60 hover:bg-white hover:shadow-md transition-all duration-300"
              >
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="h-12 w-12 bg-white border border-stone-200 flex items-center justify-center text-orange-600 group-hover:bg-orange-50 transition-colors">
                      <FileText className="h-6 w-6" />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-stone-900 group-hover:text-orange-600 transition-colors flex items-center justify-between">
                    <span>Årsrapport & regnskab</span>
                    <ArrowUpRight className="h-4 w-4 text-stone-400 group-hover:text-orange-600 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </h3>
                  <p className="text-sm text-stone-600 leading-relaxed mt-2.5">
                    Professionel opstilling af årsrapport for ApS eller enkeltmandsvirksomhed med rettidig indberetning til Erhvervsstyrelsen og Skattestyrelsen.
                  </p>
                </div>
                <div className="mt-6 pt-4 border-t border-stone-200/60 flex items-center text-xs font-bold text-orange-600 group-hover:text-orange-700">
                  <span>Læs om årsrapport &rarr;</span>
                </div>
              </Link>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Fixed Price Section */}
      <section className="py-20 bg-stone-100 border-y border-stone-200" id="priser">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <ScrollReveal direction="up">
            <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
              <p className="text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight">
                Ingen ubehagelige overraskelser. Kun faste priser.
              </p>
              <p className="text-stone-600 leading-relaxed">
                Vi tror på fuld gennemsigtighed. Derfor arbejder vi med faste, aftalte priser, så du altid ved præcis, hvad din revisor koster.
              </p>
            </div>
          </ScrollReveal>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 max-w-4xl mx-auto gap-8">
              {[...Array(2)].map((_, i) => (
                <div key={i} className="h-96 rounded-none bg-stone-200 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 max-w-4xl mx-auto gap-8 items-stretch">
              {pricingPlans.map((plan, index) => (
                <ScrollReveal 
                  key={plan.id}
                  delay={index * 150}
                  direction="up"
                  className="flex"
                >
                  <div 
                    className="relative w-full rounded-none border border-stone-200 p-8 bg-white flex flex-col justify-between transition-all duration-300 shadow-sm hover:shadow-md"
                  >
                    <div>
                      <h3 className="text-2xl font-bold text-stone-900">
                        {getLocalized(plan.title)}
                      </h3>
                      <p className="text-xs text-stone-500 mt-1.5 min-h-[32px]">
                        {getLocalized(plan.description)}
                      </p>
                      <div className="mt-6 border-b border-stone-100 pb-6">
                        <span className="text-3xl font-bold text-stone-900 tracking-tight">
                          {plan.price}
                        </span>
                      </div>

                      {/* Features list */}
                      <div 
                        className="mt-6 text-sm text-stone-600 space-y-3 prose prose-sm max-w-none prose-ul:list-none prose-ul:pl-0 prose-li:relative prose-li:before:content-['✓'] prose-li:before:absolute prose-li:before:left-0 prose-li:before:text-orange-600 prose-li:before:font-bold"
                        dangerouslySetInnerHTML={{ __html: getLocalized(plan.features) }}
                      />
                    </div>

                    <div className="mt-8 pt-6 border-t border-stone-100 flex flex-col gap-3">
                      {getServicePathForPlan(plan.title) && (
                        <Link
                          to={getServicePathForPlan(plan.title)}
                          className="w-full inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-orange-600 hover:text-orange-700 transition-colors py-1"
                        >
                          Læs mere om ydelsen
                          <ArrowRight className="h-3 w-3" />
                        </Link>
                      )}
                      <Link
                        to={`/kontakt?type=pris&plan=${encodeURIComponent(getLocalized(plan.title))}`}
                        className="w-full inline-flex items-center justify-center rounded-none px-4 py-3 text-sm font-semibold transition-all duration-300 hover:-translate-y-1 hover:shadow-lg bg-stone-100 text-stone-800 hover:bg-stone-200"
                      >
                        Vælg denne løsning
                      </Link>
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          )}

          <ScrollReveal direction="up" delay={300}>
            <div className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 border-t border-stone-200/80 text-sm text-stone-600">
              <div>
                * Alle priser er ekskl. moms. Godkendt bogføringssystem er inkluderet i priserne, hvorfor virksomhederne sparer ved ikke selv at skulle betale for det.
              </div>
              <Link
                to="/priser"
                className="shrink-0 inline-flex items-center gap-1.5 font-semibold text-orange-600 hover:text-orange-700 transition-colors"
              >
                <span>Se detaljeret pakkesammenligning</span>
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Vi tilbyder Section */}
      <section className="py-24 bg-stone-50 border-b border-stone-200 overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            {/* Image Column */}
            <div className="lg:col-span-6 order-2 lg:order-1">
              <ScrollReveal direction="left" duration={800}>
                <div className="relative">
                  <div className="relative overflow-hidden bg-stone-100 shadow-xl border border-stone-200">
                    <img
                      src="https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/assets/57eb0739-2c0e-4813-8144-be81ba542e2d/thellefsen-regnskab-raadgivning-kontor.jpg"
                      alt="Thellefsen Regnskab lyst skandinavisk mødelokale og kontor til personlig rådgivning"
                      className="w-full h-[650px] object-cover hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                </div>
              </ScrollReveal>
            </div>

            {/* Content Column */}
            <div className="lg:col-span-6 order-1 lg:order-2 space-y-8">
              <ScrollReveal direction="right" duration={800}>
                <div className="space-y-4">
                  <span className="text-xs font-bold tracking-widest uppercase text-stone-900">
                    Hvorfor vælge os
                  </span>
                  <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-stone-900 tracking-tight leading-tight">
                    Vi tilbyder mere end bare bogføring – din personlige revisor
                  </h2>
                  <p className="text-stone-600 leading-relaxed">
                    Hos Thellefsen Regnskab får du en engageret revisor og rådgiver, der sikrer, at dit regnskab altid er ajour, fejlfrit og indberettet til tiden. Vi kombinerer personlig revisorrådgivning med moderne, digitale værktøjer.
                  </p>
                </div>
              </ScrollReveal>

              <div className="divide-y divide-stone-200 border-y border-stone-200">
                <ScrollReveal direction="up" delay={100}>
                  <div className="flex gap-4 items-start py-6">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center text-stone-900">
                      <Check className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-stone-900 mb-1">Hurtig og pålidelig revisorhjælp</h3>
                      <p className="text-sm text-stone-600 leading-relaxed">
                        Vi prioriterer hurtig levering og klar dialog som din faste revisor. Du kan forvente svar på henvendelser inden for 24 timer, og har du brug for en opgave løst med kort varsel, klares hasteopgaver som regel inden for 48 timer efter aftale.
                      </p>
                    </div>
                  </div>
                </ScrollReveal>

                <ScrollReveal direction="up" delay={200}>
                  <div className="flex gap-4 items-start py-6">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center text-stone-900">
                      <Check className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-stone-900 mb-1">Fast og gennemskuelig pris</h3>
                      <p className="text-sm text-stone-600 leading-relaxed">
                        Du får en fast og konkurrencedygtig pris, så du altid kender din revisorudgift på forhånd. Det er den samme person, der står for hele dit regnskab – og som du har direkte kontakt med hele året. Det giver både overblik, tryghed og en mere personlig rådgivning.
                      </p>
                    </div>
                  </div>
                </ScrollReveal>

                <ScrollReveal direction="up" delay={300}>
                  <div className="flex gap-4 items-start py-6">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center text-stone-900">
                      <Check className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-stone-900 mb-1">Fleksibilitet og tryghed med fast revisor</h3>
                      <p className="text-sm text-stone-600 leading-relaxed">
                        Som din revisor er vi tilgængelige alle ugens syv dage og besvarer også henvendelser i weekenden. Samtidig holder vi styr på alle vigtige skatte- og momsfrister, så du trygt kan stole på, at deadlines altid overholdes.
                      </p>
                    </div>
                  </div>
                </ScrollReveal>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* SEO Information Section */}
      <section className="py-20 lg:py-24 bg-stone-100 border-b border-stone-200" id="regnskab-og-bogfoering">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <ScrollReveal direction="up">
            <div className="max-w-3xl mb-14 space-y-4">
              <span className="text-xs font-bold tracking-widest uppercase text-stone-900">
                Overblik & Rådgivning
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight">
                Søger du en revisor til bogføring og regnskab?
              </h2>
              <p className="text-base sm:text-lg text-stone-600 leading-relaxed">
                Hos Thellefsen Regnskab hjælper vi selvstændige og mindre virksomheder med at holde styr på økonomien. Du kan få hjælp til den løbende bogføring, årets regnskab eller en samlet løsning. Du har altid direkte kontakt til den samme person, som også står for opgaven og forklarer tallene, så du ved, hvad de betyder for din virksomhed.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">
            {/* Card 1 */}
            <ScrollReveal direction="up" delay={100} className="flex">
              <div className="w-full bg-white border border-stone-200 p-8 shadow-sm flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center bg-stone-100 text-stone-900 border border-stone-200">
                      <FileCheck className="h-5 w-5 text-orange-600" />
                    </div>
                    <h3 className="text-xl font-bold text-stone-900">
                      Bogføring og regnskab med en fast kontakt
                    </h3>
                  </div>
                  <div className="space-y-4 text-stone-600 leading-relaxed text-sm sm:text-base">
                    <p>
                      Bilag, bank og moms skal hænge sammen, hvis dit regnskab skal give et brugbart overblik. Vi hjælper med løbende bogføring, afstemning af banken og momsindberetning. Samtidig kan du stille spørgsmål til Peter, når du er i tvivl om en postering eller har brug for at forstå tallene.
                    </p>
                    <p>
                      Vil du samle opgaverne ét sted, tilbyder vi en regnskabspakke til en fast årlig pris. Pakken omfatter bogføring, bankafstemning, moms, årsregnskab og relevante skatteindberetninger. Adgang til et regnskabsprogram indgår også. Vi aftaler opgavens omfang, så du ved, hvad du får hjælp til, og hvad du selv skal stå for.
                    </p>
                  </div>
                  <div className="mt-6 pt-4 border-t border-stone-100 flex flex-wrap gap-4 text-xs font-semibold">
                    <Link to="/bogfoering" className="text-orange-600 hover:text-orange-700 underline underline-offset-2">
                      Læs mere om bogføring &rarr;
                    </Link>
                    <Link to="/regnskabsservice" className="text-stone-700 hover:text-orange-600 underline underline-offset-2">
                      Se samlet regnskabsservice &rarr;
                    </Link>
                  </div>
                </div>
              </div>
            </ScrollReveal>

            {/* Card 2 */}
            <ScrollReveal direction="up" delay={200} className="flex">
              <div className="w-full bg-white border border-stone-200 p-8 shadow-sm flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center bg-stone-100 text-stone-900 border border-stone-200">
                      <FileText className="h-5 w-5 text-orange-600" />
                    </div>
                    <h3 className="text-xl font-bold text-stone-900">
                      Fra årets bogføring til årsrapport og årsafslutning
                    </h3>
                  </div>
                  <div className="space-y-4 text-stone-600 leading-relaxed text-sm sm:text-base">
                    <p>
                      Driver du et selskab, hjælper vi med at gennemgå årets bogføring og opstille resultatopgørelse, balance og relevante noter til din årsrapport. Vi hjælper også med indberetningen til Erhvervsstyrelsen. Når årets poster er afstemt, er grundlaget på plads for at afslutte regnskabet.
                    </p>
                    <p>
                      Har du en enkeltmandsvirksomhed, hjælper vi med årsafslutning, regnskab og oplysningsskema. En enkeltmandsvirksomhed skal som udgangspunkt ikke indsende en offentlig årsrapport til Erhvervsstyrelsen. Du har stadig brug for et regnskab, der danner grundlag for skatteoplysningerne og viser, hvordan året er gået.
                    </p>
                  </div>
                  <div className="mt-6 pt-4 border-t border-stone-100 flex flex-wrap gap-4 text-xs font-semibold">
                    <Link to="/aarsrapport-og-aarsregnskab" className="text-orange-600 hover:text-orange-700 underline underline-offset-2">
                      Læs mere om årsrapport & årsregnskab &rarr;
                    </Link>
                  </div>
                </div>
              </div>
            </ScrollReveal>

            {/* Card 3 */}
            <ScrollReveal direction="up" delay={300} className="flex">
              <div className="w-full bg-white border border-stone-200 p-8 shadow-sm flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center bg-stone-100 text-stone-900 border border-stone-200">
                      <Calculator className="h-5 w-5 text-orange-600" />
                    </div>
                    <h3 className="text-xl font-bold text-stone-900">
                      Revisor eller bogholder, hvad har du brug for?
                    </h3>
                  </div>
                  <div className="space-y-4 text-stone-600 leading-relaxed text-sm sm:text-base">
                    <p>
                      Søger du en revisor, er det værd at afklare, hvilke opgaver du ønsker løst. Vi hjælper med bogføring, moms, årsregnskab og økonomisk sparring. Det er ikke det samme som revision. Hvis din virksomhed har pligt til revision, skal den del udføres af en godkendt revisor. Den forskel er vigtig, når du vælger hjælp.
                    </p>
                  </div>
                  <div className="mt-6 pt-4 border-t border-stone-100 flex flex-wrap gap-4 text-xs font-semibold">
                    <Link to="/ydelser" className="text-orange-600 hover:text-orange-700 underline underline-offset-2">
                      Udforsk alle ydelser &rarr;
                    </Link>
                    <Link to="/om-os" className="text-stone-700 hover:text-orange-600 underline underline-offset-2">
                      Mød din revisor Peter &rarr;
                    </Link>
                  </div>
                </div>
              </div>
            </ScrollReveal>

            {/* Card 4 */}
            <ScrollReveal direction="up" delay={400} className="flex">
              <div className="w-full bg-white border border-stone-200 p-8 shadow-sm flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center bg-stone-100 text-stone-900 border border-stone-200">
                      <Briefcase className="h-5 w-5 text-orange-600" />
                    </div>
                    <h3 className="text-xl font-bold text-stone-900">
                      Regnskabsservice i Espergærde for virksomheder i hele Danmark
                    </h3>
                  </div>
                  <div className="space-y-4 text-stone-600 leading-relaxed text-sm sm:text-base">
                    <p>
                      Vi har base i Espergærde, men din virksomhed behøver ikke ligge tæt på. Vi hjælper kunder i Nordsjælland, på Sjælland og i resten af Danmark. Det afgørende er, hvilke opgaver du vil have hjælp til. Du får den samme direkte kontakt til Peter, uanset hvor i landet du driver din virksomhed.
                    </p>
                    <p>
                      Prisen på bogføring afhænger blandt andet af antallet af bilag, opgavernes omfang og din egen indsats. Derfor tager et tilbud udgangspunkt i din virksomhed. Fortæl os, om du ønsker hjælp til enkelte opgaver eller en samlet løsning med regnskab og årsafslutning, så afklarer vi indhold og pris.
                    </p>
                  </div>
                  <div className="mt-6 pt-4 border-t border-stone-100 flex flex-wrap gap-4 text-xs font-semibold">
                    <Link to="/regnskabsservice" className="text-orange-600 hover:text-orange-700 underline underline-offset-2">
                      Læs om vores regnskabsservice &rarr;
                    </Link>
                    <Link to="/kontakt" className="text-stone-700 hover:text-orange-600 underline underline-offset-2">
                      Få et uforpligtende tilbud &rarr;
                    </Link>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>

          {/* CTA Box */}
          <ScrollReveal direction="up" delay={450}>
            <div className="mt-10 bg-white border border-stone-200 p-8 sm:p-10 shadow-sm flex flex-col lg:flex-row lg:items-center justify-between gap-6">
              <div className="max-w-3xl space-y-2">
                <h4 className="text-lg sm:text-xl font-bold text-stone-900">
                  Tag en uforpligtende samtale
                </h4>
                <p className="text-stone-600 text-sm sm:text-base leading-relaxed">
                  Er du i tvivl om, hvor du skal begynde, kan du tage en uforpligtende snak med os. Vi taler om din nuværende bogføring, de opgaver du vil af med, og hvad du gerne selv vil beholde. Kontakt os om din virksomheds regnskab, og få et tilbud med udgangspunkt i dit behov.
                </p>
              </div>
              <div className="shrink-0">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center gap-2 rounded-none bg-orange-600 px-7 py-3.5 text-sm font-semibold text-white shadow-sm hover:bg-orange-700 transition-all duration-300 hover:-translate-y-0.5"
                >
                  <span>Kontakt os</span>
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Contact CTA Section */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Text Column */}
            <div className="lg:col-span-6">
              <ScrollReveal direction="left" duration={800}>
                <div className="space-y-6">
                  <h2 className="text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight">
                    Få en uforpligtende snak med din revisor
                  </h2>
                  <p className="text-stone-600 leading-relaxed">
                    Har du spørgsmål til bogføring, moms eller årsregnskab? Eller vil du have et fast tilbud fra en erfaren revisor til din virksomhed? Tag en snak med os i dag.
                  </p>

                  <div className="space-y-4 pt-2">
                    <div className="flex items-start gap-3">
                      <Check className="h-5 w-5 text-stone-900 shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-semibold text-stone-900 text-sm">Gratis og uforpligtende</h4>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Check className="h-5 w-5 text-stone-900 shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-semibold text-stone-900 text-sm">Gennemskueligt tilbud</h4>
                        <p className="text-xs text-stone-500">Du får altid en fast, skriftlig pris inden vi går i gang.</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Check className="h-5 w-5 text-stone-900 shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-semibold text-stone-900 text-sm">Direkte kontakt</h4>
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-stone-200/80">
                    <div className="font-semibold text-stone-900 text-sm">Ring direkte til os:</div>
                    <a href="tel:+4531335470" className="text-2xl font-bold text-orange-600 hover:underline transition-colors mt-1 block">
                      31 33 54 70
                    </a>
                    <p className="text-xs text-stone-500 mt-1">Hverdage kl. 08:30 - 16:30 (Fredag til 15:30)</p>
                  </div>
                </div>
              </ScrollReveal>
            </div>

            {/* Form Column */}
            <div className="lg:col-span-6">
              <ScrollReveal direction="right" duration={800}>
                <ContactForm />
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-stone-100 border-t border-stone-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <ScrollReveal direction="up">
            <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
              <p className="text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight">
                Hvad kunderne siger om deres faste revisor
              </p>
              <p className="text-stone-600 leading-relaxed">
                Vi lægger stor vægt på personlig revisorkontakt, hurtig respons og faste, gennemskuelige priser. Se hvad vores kunder oplever i hverdagen.
              </p>
            </div>
          </ScrollReveal>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-48 bg-stone-100 rounded-none animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="relative">
              <div className="overflow-hidden -mx-4 px-4">
                <div 
                  className={`flex ${isTransitioning ? 'transition-transform duration-500 ease-in-out' : ''}`}
                  style={{ 
                    transform: `translateX(-${testimonialIndex * (100 / (testimonials.length + itemsToShow))}%)${isTransitioning ? '' : ' !important'}`,
                    width: `${((testimonials.length + itemsToShow) / itemsToShow) * 100}%`
                  }}
                >
                  {[...testimonials, ...testimonials.slice(0, itemsToShow)].map((testimonial, idx) => (
                    <div 
                      key={`${testimonial.id}-${idx}`}
                      className="px-4 shrink-0 transition-all duration-300"
                      style={{ width: `${100 / (testimonials.length + itemsToShow)}%` }}
                    >
                      <TestimonialCard testimonial={testimonial} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <ScrollReveal direction="up" delay={200}>
            <div className="text-center mt-12">
              <Link
                to="/kunderne-siger"
                className="inline-flex items-center gap-2 text-sm font-semibold text-stone-900 hover:underline"
              >
                Læs alle kundeanmeldelser
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  )
}
